import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogoLockup } from "../components/Logo";
import { ROLES, ROLE_ORDER } from "../lib/roles";
import { useRole } from "../context/RoleContext";
import { listArchitectApplicants } from "../lib/applications";
import { api } from "../lib/api";
import {
  ArrowRight,
  Eye,
  Hammer,
  Compass,
  Landmark,
  ShieldCheck,
  Send,
  ChevronDown,
  ChevronUp,
  Users,
} from "lucide-react";

const ROLE_ICONS = {
  anonymous: Eye,
  owner_builder: Hammer,
  architect: Compass,
  city_staff: Landmark,
  platform_admin: ShieldCheck,
};

export default function SignIn() {
  const navigate = useNavigate();
  const { signIn, signInAsUser } = useRole();
  const [selected, setSelected] = useState("owner_builder");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [resumeOpen, setResumeOpen] = useState(false);
  const [applicants, setApplicants] = useState(null);
  // "Continue as a specific user" — flips on for owner_builder + architect.
  const [switchOpen, setSwitchOpen] = useState(false);
  const [allUsers, setAllUsers] = useState(null);

  useEffect(() => {
    if (resumeOpen && applicants == null) {
      listArchitectApplicants()
        .then(setApplicants)
        .catch(() => setApplicants([]));
    }
  }, [resumeOpen, applicants]);

  useEffect(() => {
    if (switchOpen && allUsers == null) {
      api.get("/users")
        .then((r) => setAllUsers(r.data || []))
        .catch(() => setAllUsers([]));
    }
  }, [switchOpen, allUsers]);

  // Wrapped setter so changing the role auto-closes the switch picker
  // without needing a state-in-effect hack.
  function selectRole(next) {
    setSelected(next);
    setSwitchOpen(false);
  }

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const res = await signIn(selected);
      // Every signed-in role lands on its Dashboard — `/{role-home}` IS the
      // dashboard for that role. The single exception is an architect whose
      // application is not yet approved; they land on their application
      // status page.
      const DEFAULT_HOME = {
        anonymous: "/",
        owner_builder: "/owner",
        architect: "/architect",
        city_staff: "/city",
        platform_admin: "/admin",
      };
      let home = DEFAULT_HOME[selected] || "/";
      if (
        selected === "architect" &&
        res?.user?.account_status &&
        res.user.account_status !== "active"
      ) {
        home = "/architect/application";
      }
      navigate(home);
    } catch (e) {
      setError("Could not sign in. Please confirm the backend is running.");
    } finally {
      setSubmitting(false);
    }
  };

  const resumeAs = async (userId) => {
    setSubmitting(true);
    setError("");
    try {
      const res = await signInAsUser(userId);
      if (res?.user?.account_status === "active") {
        navigate("/architect");
      } else {
        navigate("/architect/application");
      }
    } catch (e) {
      setError("Could not resume this application.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="wn-page min-h-screen" data-testid="sign-in-page">
      {/* Slim, solid indigo top bar (no gradient) */}
      <div style={{ backgroundColor: "#1B1F4B" }}>
        <div className="max-w-[1280px] mx-auto px-6 h-16 flex items-center">
          <Link to="/" data-testid="sign-in-brand">
            <LogoLockup tone="white" size={28} />
          </Link>
        </div>
      </div>

      <div className="max-w-[1280px] mx-auto px-6 py-16 grid lg:grid-cols-[1.05fr_1fr] gap-14 items-start">
        {/* Left: editorial intro */}
        <div className="max-w-[560px]">
          <div className="wn-label" style={{ color: "#8F3C23" }}>
            Proof of concept · Indianapolis
          </div>
          <h1
            className="mt-4"
            style={{ fontSize: "clamp(40px, 5.2vw, 52px)", fontWeight: 500 }}
          >
            Pre-reviewed homes,<br />
            <span style={{ color: "#B14E2F" }}>matched to real parcels.</span>
          </h1>
          <p
            className="mt-6"
            style={{ color: "#6C6B85", fontSize: 16, lineHeight: 1.65, maxWidth: 520 }}
          >
            WHOLE Neighborhoods is a civic marketplace connecting homeowners,
            CDCs, licensed architects, and city staff around a catalog of pre-reviewed
            infill designs and a database of vacant parcels.
          </p>

          <div className="mt-10 wn-card p-6" data-testid="sign-in-intro-card">
            <div className="wn-label">How this PoC works</div>
            <ul className="mt-3 space-y-2" style={{ fontSize: 14, color: "#272636" }}>
              <li>· Pick a role to enter — no passwords, no account creation.</li>
              <li>· Anonymous visitors browse the marketplace, parcels, and architects.</li>
              <li>· Every other role can act on records relevant to that role.</li>
            </ul>
          </div>
        </div>

        {/* Right: role picker */}
        <div
          className="wn-card p-8"
          data-testid="role-picker-card"
          style={{ borderColor: "#D9D8E4" }}
        >
          <div className="wn-label">Sign in</div>
          <h2 className="mt-2" style={{ fontSize: 26 }}>
            Choose how you’ll enter
          </h2>
          <p className="mt-2" style={{ color: "#6C6B85", fontSize: 14 }}>
            Select a role to continue. You can sign out and switch at any time.
          </p>

          <div
            className="mt-6 grid gap-3"
            role="radiogroup"
            aria-label="Role"
            data-testid="role-options"
          >
            {ROLE_ORDER.map((key) => {
              const r = ROLES[key];
              const Icon = ROLE_ICONS[key];
              const isSelected = selected === key;
              return (
                <button
                  key={key}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  onClick={() => selectRole(key)}
                  data-testid={`role-option-${key}`}
                  className="text-left transition-colors"
                  style={{
                    backgroundColor: isSelected ? "rgba(177,78,47,0.06)" : "#FBFAFD",
                    border: `1px solid ${isSelected ? "#B14E2F" : "#D9D8E4"}`,
                    borderRadius: 10,
                    padding: "14px 16px",
                    display: "grid",
                    gridTemplateColumns: "32px 1fr auto",
                    alignItems: "center",
                    columnGap: 14,
                    cursor: "pointer",
                  }}
                >
                  <span
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      backgroundColor: isSelected ? "#B14E2F" : "rgba(27,31,75,0.06)",
                      color: isSelected ? "#FBFAFD" : "#1B1F4B",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon size={18} />
                  </span>
                  <span>
                    <span
                      style={{ display: "block", fontWeight: 700, color: "#1B1F4B", fontSize: 14 }}
                    >
                      {r.label}
                    </span>
                    <span style={{ display: "block", color: "#6C6B85", fontSize: 13, marginTop: 2 }}>
                      {r.blurb}
                    </span>
                  </span>
                  <span
                    aria-hidden
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: 999,
                      border: `2px solid ${isSelected ? "#B14E2F" : "#9AA0C0"}`,
                      backgroundColor: isSelected ? "#B14E2F" : "transparent",
                    }}
                  />
                </button>
              );
            })}
          </div>

          {error && (
            <div
              className="mt-4 wn-pill wn-pill-error"
              data-testid="sign-in-error"
            >
              {error}
            </div>
          )}

          {/* "Continue as a specific {role}" — only for owner / architect
              roles where multiple seeded identities live in the demo DB. */}
          {(selected === "owner_builder" || selected === "architect") && (
            <div className="mt-5">
              <button
                type="button"
                onClick={() => setSwitchOpen((o) => !o)}
                className="inline-flex items-center gap-2"
                style={{
                  color: "#5B6196",
                  fontSize: 12.5,
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  background: "transparent",
                  border: 0,
                  cursor: "pointer",
                }}
                data-testid="switch-user-toggle"
              >
                {switchOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                <Users size={13} />
                Continue as a different{" "}
                {selected === "owner_builder" ? "owner / builder" : "architect"}
              </button>
              {switchOpen && (
                <div
                  className="mt-3"
                  data-testid="switch-user-picker"
                  style={{
                    border: "1px solid #D9D8E4",
                    borderRadius: 8,
                    padding: 10,
                    background: "#FBFAFD",
                  }}
                >
                  {allUsers == null && (
                    <div style={{ fontSize: 12, color: "#6C6B85" }}>
                      Loading users…
                    </div>
                  )}
                  {allUsers && (
                    <SwitchUserList
                      users={allUsers}
                      role={selected}
                      onPick={async (userId) => {
                        setSubmitting(true);
                        setError("");
                        try {
                          const res = await signInAsUser(userId);
                          const home = selected === "owner_builder"
                            ? "/owner"
                            : (res?.user?.account_status === "active"
                                ? "/architect"
                                : "/architect/application");
                          navigate(home);
                        } catch (e) {
                          setError("Could not sign in as this user.");
                        } finally {
                          setSubmitting(false);
                        }
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          )}

          <button
            onClick={submit}
            disabled={submitting}
            className="wn-btn wn-btn-accent mt-7 w-full"
            data-testid="continue-btn"
          >
            {submitting ? "Signing in…" : "Continue"}
            <ArrowRight size={16} />
          </button>

          {/* Apply link — public flow for new architects */}
          <div className="mt-6 pt-5" style={{ borderTop: "1px solid #D9D8E4" }}>
            <Link
              to="/register/architect"
              className="inline-flex items-center gap-2"
              style={{
                color: "#1B1F4B",
                fontSize: 13,
                fontWeight: 700,
                textDecoration: "none",
              }}
              data-testid="apply-architect-link"
            >
              <Send size={14} />
              Are you an architect? Apply to join the network.
            </Link>
          </div>

          {/* Resume application disclosure */}
          <div className="mt-3">
            <button
              type="button"
              onClick={() => setResumeOpen((o) => !o)}
              className="inline-flex items-center gap-2"
              style={{
                color: "#6C6B85",
                fontSize: 12,
                fontWeight: 600,
                background: "transparent",
                border: 0,
                cursor: "pointer",
              }}
              data-testid="resume-toggle"
            >
              {resumeOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              Returning to update an application? Resume here.
            </button>
            {resumeOpen && (
              <div
                className="mt-3 wn-card"
                style={{ padding: 12 }}
                data-testid="resume-picker"
              >
                {applicants == null && (
                  <div style={{ fontSize: 12, color: "#6C6B85" }}>
                    Loading applicants…
                  </div>
                )}
                {applicants && applicants.length === 0 && (
                  <div style={{ fontSize: 12, color: "#6C6B85" }}>
                    No pending applicants on file.
                  </div>
                )}
                <div className="space-y-2">
                  {(applicants || []).map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => resumeAs(a.id)}
                      data-testid={`resume-as-${a.id}`}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        padding: "8px 10px",
                        borderRadius: 8,
                        border: "1px solid #D9D8E4",
                        backgroundColor: "#FBFAFD",
                        cursor: "pointer",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: 13,
                          color: "#1B1F4B",
                        }}
                      >
                        {a.name}
                      </div>
                      <div style={{ fontSize: 12, color: "#6C6B85" }}>
                        {a.organization} · {a.account_status}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="mt-4 text-center" style={{ fontSize: 12, color: "#6C6B85" }}>
            This is a proof of concept — no authentication required.
          </div>
        </div>
      </div>
    </div>
  );
}

function SwitchUserList({ users, role, onPick }) {
  const filtered = (users || [])
    .filter((u) => u.role === role)
    // Only surface active accounts so we don't accidentally sign in as a
    // pending architect (those go through the Resume flow instead).
    .filter((u) =>
      u.account_status === undefined || u.account_status === "active"
    )
    // Headline user first, then alphabetical.
    .sort((a, b) => {
      if (a.is_headline && !b.is_headline) return -1;
      if (b.is_headline && !a.is_headline) return 1;
      return (a.name || "").localeCompare(b.name || "");
    });

  if (filtered.length === 0) {
    return (
      <div style={{ fontSize: 12, color: "#6C6B85" }}>
        No additional {role === "owner_builder" ? "owners" : "architects"}{" "}
        seeded in the demo database.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {filtered.map((u) => (
        <button
          key={u.id}
          type="button"
          onClick={() => onPick(u.id)}
          data-testid={`switch-user-as-${u.id}`}
          style={{
            width: "100%",
            textAlign: "left",
            padding: "10px 12px",
            borderRadius: 8,
            border: "1px solid #D9D8E4",
            backgroundColor: "#FBFAFD",
            cursor: "pointer",
          }}
        >
          <div
            style={{
              fontWeight: 700,
              fontSize: 13,
              color: "#1B1F4B",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            {u.name}
            {u.is_headline && (
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "#8F3C23",
                  background: "rgba(177,78,47,0.10)",
                  padding: "2px 6px",
                  borderRadius: 999,
                }}
              >
                Default
              </span>
            )}
          </div>
          <div
            style={{
              fontSize: 12,
              color: "#6C6B85",
              marginTop: 2,
            }}
          >
            {u.organization || (role === "architect"
              ? "Independent practice"
              : "Individual owner")}
            {u.email ? ` · ${u.email}` : ""}
          </div>
        </button>
      ))}
    </div>
  );
}
